import { useEffect, useState } from 'react'
import { api, type AttestationDue, type Guidelines, type ReviewItem, type Rule } from '../api'
import { Cited } from '../components/Cited'
import { useFunds } from '../components/FundPicker'
import { FilterHeader, SortHeader, sortRows, useSort } from '../components/Grid'
import { Origin } from '../components/Origin'
import { RowMenu } from '../components/RowMenu'
import { layerLabel, says, subjectText } from '../limits'
import { DeskCell, DeskHeader, useDesks } from '../desks'

/**
 * Portfolio attestation — TECHNICAL_DESIGN.md §9.4, §9.3.
 *
 * The owner asserts two things over the whole resolved set: that every rule is
 * faithful to its source, and that **nothing binding is missing**. Only the
 * first is something the system can evidence; the second is the entire reason a
 * person is asked, which is why the screen states them separately and requires
 * both to be ticked. A single "I attest" box would collapse the claim nobody can
 * verify into the one the store already knows.
 *
 * **The screen opens on which funds are waiting.** `portfolio.attestation_due_on`
 * has been maintained since `0007` — attesting pushes it out by the cycle, a
 * membership change pulls it in (§10.1's compensating control), §12.2 counts the
 * overdue ones — and nothing had ever shown it to the person who has to act. The
 * screen asked for a fund by name, so the workflow began with the reader already
 * knowing the answer to the question the screen exists to answer. §9.2's review
 * queue has always been a queue; this is the same obligation on a longer clock.
 *
 * **The resolved set is shown, inherited rules included and not separable.** A
 * statement excluding them would assert completeness over a set the owner had
 * not been shown — the precise failure the assertion exists to prevent. That
 * argument was written here from the start and the screen printed a *count*: the
 * owner was asked to certify that every rule is faithful to its source, having
 * been shown the number forty. Now they are shown the forty.
 *
 * What the owner is asked about inherited rules is narrower and the screen says
 * so: not *is this firm policy correct* — that is the scope rule's owner's job
 * under §9.2 — but *is my mandate consistent with it*.
 *
 * **Blockers are shown before the statement, not as a refusal after it.** An
 * overdue rule anywhere in the resolved set blocks attestation, and where the
 * blocker is inherited the owner genuinely cannot act on it — so the screen names
 * whose it is rather than presenting a wall.
 */
function flatten(guidelines: Guidelines | null): Rule[] {
  return (guidelines?.constraints ?? []).flatMap((c) => [
    ...(c.binding ? [c.binding] : []),
    ...c.also,
  ])
}

export function Attest() {
  const { funds } = useFunds()
  const [waiting, setWaiting] = useState<AttestationDue[]>([])
  const [fundId, setFundId] = useState('')
  const [blockers, setBlockers] = useState<ReviewItem[]>([])
  const [guidelines, setGuidelines] = useState<Guidelines | null>(null)
  const [accuracy, setAccuracy] = useState(false)
  const [completeness, setCompleteness] = useState(false)
  const [note, setNote] = useState('')
  const [done, setDone] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** How far ahead to look. Nothing by default — a queue listing what is not
   *  owed yet is a queue nobody can act on, and the first version of this
   *  returned every fund carrying a date, so a fund signed off this morning
   *  came straight back scheduled for next year under a heading saying it was
   *  waiting. */
  const [ahead, setAhead] = useState(0)
  /* The queue *is* the screen, so its own column narrows it — there is no
     second control naming a fund. A separate picker offered every fund whether
     or not anything was owed on it, which made the list beside it look like a
     suggestion; and looking ahead already surfaces a fund that is not due yet,
     which is the honest way to sign one off early. */
  const [fundFilter, setFundFilter] = useState('')
  const { sort, onSort } = useSort()
  /** The desk the queue is narrowed to, for somebody across the whole firm (decision 91). */
  const [desk, setDesk] = useState('')
  const desks = useDesks()

  const selected = funds.find((f) => f.id === fundId) ?? null

  /** Returns whether it worked, rather than reporting for itself. Refreshing
   *  the queue is a follow-up to the sign-off, and a failure here is not the
   *  sign-off failing — reported as one it says the opposite of what happened,
   *  which is the worst available error message. */
  async function loadQueue(days = ahead): Promise<string | null> {
    try {
      setWaiting(await api.attestationsDue(days))
      return null
    } catch (e) {
      return e instanceof Error ? e.message : String(e)
    }
  }

  useEffect(() => {
    void loadQueue(ahead).then((e) => e && setError(e))
  }, [ahead])

  useEffect(() => {
    if (!fundId) {
      setGuidelines(null)
      setBlockers([])
      return
    }
    let live = true
    setError(null)
    setDone(null)
    setAccuracy(false)
    setCompleteness(false)
    Promise.all([api.attestationBlockers(fundId), api.guidelines(fundId)])
      .then(([b, g]) => {
        if (!live) return
        setBlockers(b)
        setGuidelines(g)
      })
      .catch((e) => live && setError(String(e.message ?? e)))
    return () => {
      live = false
    }
  }, [fundId])

  async function submit() {
    if (!fundId) return
    setBusy(true)
    setError(null)
    try {
      const result = await api.attest(fundId, note)
      setDone(`Recorded over ${result.rule_count} rule(s).`)
      setNote('')
      // Said separately, and after the success. The sign-off is on the record
      // whatever the queue does next.
      const stale = await loadQueue()
      if (stale) setError(`Signed off. The waiting list could not be refreshed — ${stale}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const rules = flatten(guidelines)
  const inherited = rules.filter((r) => r.inherited)

  const queue = sortRows(
    waiting.filter((w) => (!fundFilter || w.code === fundFilter) && (!desk || desks.fund(w.code) === desk)),
    sort,
    {
      desk: (w) => desks.fund(w.code) ?? '',
      fund: (w) => w.code,
      due: (w) => w.due_on ?? '',
      last: (w) => w.last_attested_at ?? '',
    },
  )

  /* Two modes, not one page. Picking a fund replaces the queue rather than
     appending to it: the sign-off is a page of reading — the whole resolved set,
     the blockers, two assertions — and leaving the list of other funds above it
     puts the next piece of work in the reader's eye while they are being asked
     to certify this one. A way back, always. */
  if (fundId) {
    return (
      <div className="attest">
        <header className="page-head">
          <button className="ghost" onClick={() => setFundId('')}>
            ← All funds waiting
          </button>
          <h2>
            Sign off <code>{selected?.code ?? ''}</code>
          </h2>
          <p className="note">
            State that these limits are faithful to the documents behind them and
            that none is missing. The second half is the reason a person is
            asked: no system can check it.
          </p>
        </header>

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        {done && <p className="done">{done}</p>}


        <section>
          {blockers.length > 0 ? (
            <div className="warning" data-testid="blockers">
              <h3>{blockers.length} rule(s) in this set are overdue for review</h3>
              <p>
                Attesting to a set that has not been checked looks like assurance,
                so this is blocked until they are confirmed.
              </p>
              <ul>
                {blockers.map((b) => (
                  <li key={b.rule_id}>
                    <code>{b.rule_key}</code> — {b.days_overdue} day(s) overdue
                    {b.scope_id && (
                      <span className="inherited" data-testid="blocker-inherited">
                        {' '}
                        firm policy on {b.scope_code} — not yours to confirm; its
                        owner reviews it
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <>
              <p className="summary" data-testid="summary">
                {rules.length} rule(s) in the resolved set, {inherited.length} of
                them inherited. This is what you are signing off — read it.
              </p>
              {inherited.length > 0 && (
                <p className="note" data-testid="inherited-note">
                  Inherited rules are part of this statement and cannot be
                  excluded from it. You are not attesting that firm policy is
                  correct — that is its own owner's job — but that your mandate is
                  consistent with it, and that this portfolio belongs in the
                  scopes it is a member of.
                </p>
              )}

              {/* The set itself. The argument for showing it was written into
                  this file from the start and the screen printed a count: the
                  owner was asked to certify that every rule is faithful to its
                  source, having been shown the number forty. */}
              {rules.length > 0 && (
                <table className="grid limits has-menus" data-testid="attested-set">
                  <thead>
                    <tr>
                      <th>Constrains</th>
                      <th>Limit</th>
                      <th>Says</th>
                      <th>Set by</th>
                      <th>From</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules.map((rule) => {
                      const said = says(rule)
                      return (
                        <tr key={rule.id} data-testid="attested-rule">
                          <td>
                            {rule.measure ?? 'narrative'}
                            <span className="subject">{subjectText(rule.subject)}</span>
                          </td>
                          <td>
                            <code>{rule.rule_key}</code>
                          </td>
                          <td>
                            <span className={said.numeric ? 'limit' : 'no-number'}>
                              {said.text}
                            </span>
                          </td>
                          <td>
                            <span className="pill pill-layer">
                              {layerLabel(rule.layer)}
                            </span>
                          </td>
                          <td>
                            <Origin rule={rule} />
                            <Cited rule={rule} />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}

              <div className="card">
                <label className="assert">
                  <input
                    type="checkbox"
                    checked={accuracy}
                    onChange={(e) => setAccuracy(e.target.checked)}
                  />
                  <b>Accuracy.</b> Every rule recorded here is faithful to its
                  source document.
                </label>
                <label className="assert">
                  <input
                    type="checkbox"
                    checked={completeness}
                    onChange={(e) => setCompleteness(e.target.checked)}
                  />
                  <b>Completeness.</b> Nothing binding is missing from this set.
                  <span className="note">
                    {' '}
                    No system can verify this. It is your claim, not a finding.
                  </span>
                </label>
                <label>
                  Note (optional)
                  <input value={note} onChange={(e) => setNote(e.target.value)} />
                </label>

                <div className="actions">
                  <button
                    disabled={!accuracy || !completeness || busy}
                    onClick={() => void submit()}
                  >
                    {busy ? 'Recording…' : `Sign off ${selected?.code ?? 'this fund'}`}
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    )
  }

  return (
    <div className="attest">
      <header className="page-head">
        <h2>Funds due for sign-off</h2>
        <p className="note">
          Once a cycle, the person who owns a fund states that its limits are
          faithful to the documents behind them and that none is missing. These
          are the funds that owe one.
        </p>
      </header>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {done && <p className="done">{done}</p>}

      {/* The queue this screen never had, and the only fund control on it: the
          column narrows the list rather than a picker beside it naming funds the
          list does not contain. Looking ahead is how a fund not yet due is
          reached, which is also the honest way to sign one off early. */}
      <section className="card" data-testid="attestations-due">
        <h3>
          {waiting.length === 0
            ? ahead > 0
              ? `Nothing owed in the next ${ahead} days`
              : 'Nothing owed'
            : `${waiting.length} fund(s) to sign off`}
        </h3>
        <div className="filters">
          <label className="ahead">
            Look ahead (days)
            <input
              type="number"
              min={0}
              max={365}
              value={ahead}
              title="Nothing by default: the queue is what is owed now. Look ahead to plan."
              onChange={(e) => setAhead(Number(e.target.value))}
            />
          </label>
        </div>
        {waiting.length > 0 && (
          <table className="grid has-menus">
            <thead>
              <tr>
                <DeskHeader sort={sort} onSort={onSort} value={desk} onChange={setDesk} />
                <FilterHeader
                  label="Portfolio"
                  sortKey="fund"
                  sort={sort}
                  onSort={onSort}
                  value={fundFilter}
                  onChange={setFundFilter}
                  all="Every fund waiting"
                  testId="filter-portfolio"
                  options={waiting.map((w) => ({ value: w.code, label: w.code }))}
                />
                <SortHeader label="Due" sortKey="due" sort={sort} onSort={onSort} />
                <SortHeader
                  label="Last signed off"
                  sortKey="last"
                  sort={sort}
                  onSort={onSort}
                />
                <th />
              </tr>
            </thead>
            <tbody>
              {queue.map((row) => (
                <tr key={row.portfolio_id} data-testid="due-row">
                  <DeskCell fund={row.code} />
                  <td>
                    {/* The code on its own line and what it carries beneath, rather
                        than three inline spans that ran together as "BRAETON12
                        limit(s)". */}
                    <span className="fund-cell">
                      <span className="portfolio">{row.code}</span>
                      <span className="fund-meta">
                        {/* The name only when it adds something — a fund imported
                            from a workbook column usually has the code for a name. */}
                        {row.name !== row.code && `${row.name} · `}
                        {row.rule_count} {row.rule_count === 1 ? 'limit' : 'limits'}
                      </span>
                    </span>
                  </td>
                  <td>
                    {row.due_on ? (
                      <>
                        {new Date(row.due_on).toLocaleDateString()}
                        {row.days_overdue > 0 && (
                          <span className="warn">
                            {row.days_overdue} day(s) overdue
                          </span>
                        )}
                      </>
                    ) : (
                      /* No date to be late against, which is why a query keyed
                         on the date alone excluded exactly this case. */
                      <span className="warn" data-testid="never-signed">
                        never signed off
                      </span>
                    )}
                  </td>
                  <td className="clause">
                    {row.last_attested_at
                      ? new Date(row.last_attested_at).toLocaleDateString()
                      : '—'}
                  </td>
                  <td className="run-actions">
                    <RowMenu
                      label={`Actions for ${row.code}`}
                      testId="due-menu"
                      items={[
                        {
                          label: fundId === row.portfolio_id ? 'Open' : 'Sign off',
                          onSelect: () => setFundId(row.portfolio_id),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

    </div>
  )
}
